/**
 * Runs `reconcileCloudItems` (plans/cloudItemsReconcile.js) once the server
 * has said what the signed-in plan allows, and again whenever that answer
 * changes (a downgrade, a lapsed plan, an upgrade). A pass that could not run
 * yet — the sync engine has not taken the session — is retried a few times.
 * Mounted once from App.js.
 */
import { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useEntitlementsStore } from '../store/entitlementsStore';
import { reconcileCloudItems } from '../plans/cloudItemsReconcile';

const RETRY_MS = 5000;
const MAX_TRIES = 4;

export function useCloudItemsReconcile() {
  const userId = useAuthStore((s) => (s.isAuthenticated && s.user?.id != null ? String(s.user.id) : null));
  const source = useEntitlementsStore((s) => s.source);
  const capsKnown = useEntitlementsStore((s) => s.capabilitiesKnown);
  const caps = useEntitlementsStore((s) => (s.capabilities || []).join(','));

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    let timer = null;
    const run = async (tries) => {
      const result = await reconcileCloudItems();
      if (cancelled || result.action !== 'failed' || tries + 1 >= MAX_TRIES) return;
      timer = setTimeout(() => run(tries + 1), RETRY_MS);
    };
    run(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [userId, source, capsKnown, caps]);
}
